import { Injectable } from '@angular/core';
import { Repair } from '../../models/repair.model';
import { Equipment } from '../../models/equipment.model';
import { DamageType } from '../../models/mode.model';
import { catchError, distinctUntilChanged, Observable, take } from 'rxjs';
import { MasterDataService } from '../../services/master-data.service';
import { RepairExclusion } from '../../models/repair-exclusion.model';
import { ContainerGrade } from '../../models/container-grade.model';
import { ThirdPartyIndicator } from '../../models/tpi.model';
import { RepairLocation } from '../../models/repair-location.model';
import { CodeDescription } from '../../models/code-description.model';
import { Currency } from '../../models/currency.model';

@Injectable()
export class RepairLineItemsDataService {
  constructor(private masterDataService: MasterDataService) {}
  getRepairExclusions(repairModeCode: string): Observable<RepairExclusion[]> {
    const repairExclusions = this.masterDataService.getRepairExclusions(repairModeCode).pipe(
      catchError((error) => {
        throw error;
      }),
      distinctUntilChanged(),
      take(1),
    );
    return repairExclusions;
  }

  /**
   * Section get container grades
   */
  getContainerGrades(): Observable<ContainerGrade[]> {
    const containerGrades = this.masterDataService.getContainerGrades().pipe(
      take(1),
      catchError((error) => {
        throw error;
      }),
      distinctUntilChanged(),
    );
    return containerGrades;
  }
  /**
   * section to get tpi codes
   *
   */
  getTPIs(): Observable<ThirdPartyIndicator[]> {
    const tpis = this.masterDataService.getRepairTpiCodes().pipe(
      catchError((error) => {
        throw error;
      }),
      distinctUntilChanged(),
      take(1),
    );
    return tpis;
  }
  /**
   * Section to get repir Loactions
   */
  getRepairLocations(): Observable<RepairLocation[]> {
    const repairLocations = this.masterDataService.getRepairLocations().pipe(
      take(1),
      catchError((error) => {
        throw error;
      }),
      distinctUntilChanged(),
    );
    return repairLocations;
  }
  /**
   * Section to get damage codes
   */
  getDamages(damageType: DamageType): Observable<CodeDescription[]> {
    const damages = this.masterDataService.getDamageCodes(damageType).pipe(
      catchError((error) => {
        throw error;
      }),
      distinctUntilChanged(),
      take(1),
    );
    return damages;
  }

  /**
   * Section to get repair codes
   * @param shopCode
   * @param damageType
   * @param equipment
   * @returns
   */
  getRepairs(shopCode: string, repairModeCode: string, currency: Currency): Observable<Repair[]> {
    const repairs = this.masterDataService.getRepairCodes(repairModeCode, shopCode, currency.exchangeRateFromUsd ?? 0).pipe(
      catchError((error) => {
        throw error;
      }),
      distinctUntilChanged(),
      take(1),
    );
    return repairs;
  }

  /**
   * Filter repair codes by container size
   * @param repairs
   * @param equipment
   * @returns Filtered STS Codes
   */
  public filterRepairCodesByEquipmentSize(repairs: Repair[], equipment: Equipment): Repair[] {
    let filteredRepairs = repairs;
    const sizeOfEquipment: number = Number(equipment.equipmentSize ?? 0);
    if (equipment.equipmentTypeParentCode === 'CONT' && sizeOfEquipment > 0) {
      filteredRepairs = repairs.filter((x) => x.restrictedContainerSizes == null || !x.restrictedContainerSizes.includes(sizeOfEquipment));
    }
    return filteredRepairs;
  }

  /**
   * Filter repair codes by equipment manufacturer and model
   * The reefre modes are STARCOOL, DAIKIN, CARRIER, THERMO KING
   * STARCOOL and CARRIER have multiple sub models
   * STARCOOL has Star Freeze(SF) and Star Cool(SC)
   * CARRIER has Naturaline and other models
   * @param repairs
   * @param equipment
   * @param damageType
   * @returns Filtered STS Codes
   */
  public filterRepairCodesByEquipmentManufacturerAndModel(repairs: Repair[], equipment: Equipment, damageType: DamageType): Repair[] {
    let filteredRepairs = repairs;
    if (equipment.equipmentTypeCode === 'REEF' && damageType === DamageType.REEFER_UNIT) {
      switch (equipment.reeferUnitManufacturerName?.toUpperCase()) {
        case 'STARCOOL':
          if (equipment.reeferUnitModel?.toUpperCase().startsWith('SF')) {
            filteredRepairs = repairs.filter((x) => x.code.startsWith('0') || x.code.startsWith('7'));
          } else {
            filteredRepairs = repairs.filter((x) => x.code.startsWith('0') || x.code.startsWith('1'));
          }
          break;
        case 'DAIKIN':
          filteredRepairs = repairs.filter((x) => x.code.startsWith('0') || x.code.startsWith('2'));
          break;
        case 'CARRIER':
          if (equipment.reeferUnitModel?.toUpperCase() === 'NATURALINE') {
            filteredRepairs = repairs.filter((x) => x.code.startsWith('0') || x.code.startsWith('4'));
          } else {
            filteredRepairs = repairs.filter((x) => x.code.startsWith('0') || x.code.startsWith('5'));
          }
          break;
        case 'THERMO KING':
          filteredRepairs = repairs.filter((x) => x.code.startsWith('0') || x.code.startsWith('6'));
          break;
      }
    }
    return filteredRepairs;
  }
}
